import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect
} from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Alert,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
  Image,
  TouchableOpacity
} from "react-native";
import type { TextInput as RNTextInput } from "react-native";
import { useTranslation } from "react-i18next";
import Colors from "../../../../../constants/colors";
import { consumerLookup } from "../../../../../services/send.service";
import { SessionState, useSessionStore } from "../../../../../../../state/src/useSessionStore";
import EnglishFlag from "../../../../../../../../assets/images/english-flag.png";
import RightArrow from "../../../../../../../../assets/images/rightArrow.png";


type Props = {
  onValidationChange?: (valid: boolean) => void;
};

export type EnterPhoneStepHandle = {
  submit: () => Promise<"ok" | "notfound">;
};

const AccountInformationStep = forwardRef<EnterPhoneStepHandle, Props>(({ onValidationChange }, ref) => {
  const { t } = useTranslation();
  const isBillPay = useSessionStore((s: SessionState) => (s as any).isBillPay);
  const selectedBillerInformation = useSessionStore((s: SessionState) => (s as any).selectedBillerInformationStore);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const setIsNewConsumer = useSessionStore((s: SessionState) => (s as any).setIsNewConsumer);
  const [phoneNumber, setPhoneNumber] = useState("");
  const storedBillerAccountNumber = useSessionStore((s: SessionState) => (s as any).billPayBillerAccountNumberStore);
  const setBillPayBillerAccountNumberStore = useSessionStore((s: SessionState) => (s as any).setBillPayBillerAccountNumberStore);
  const [billerAccountNumber, setBillerAccountNumber] = useState(String(storedBillerAccountNumber ?? ""));
  const inputRef = useRef<RNTextInput>(null);
  const isValid = billerAccountNumber != "" ;
        
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  const submit = async (): Promise<"ok" | "notfound"> => {
    if (phoneNumber.length !== 10) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_INVALID"));
      throw new Error("Invalid phone number");
    }
    if (!agentId || !unitProfileId) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_MISSING_LOCATION"));
      throw new Error("Missing agent or unit profile");
    }
    try {
      await consumerLookup(phoneNumber, agentId, unitProfileId);
      setIsNewConsumer?.(false);
      return "ok";
    } catch (error: any) {
      const code = error?.code || error?.ErrorDetail?.ErrorCode;
      if (String(code) === "616") {
        setIsNewConsumer?.(true);
        return "notfound";
      }
      setIsNewConsumer?.(false);
      Alert.alert("Error", t("PHONE_STEP_ERROR_FAILED"));
      throw error;
    }
  };

  useImperativeHandle(ref, () => ({ submit }));

  const Wrapper = Platform.OS === "web" ? View : View;
  return (
    <Wrapper style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <ScrollView
          contentContainerStyle={[styles.scrollContainer, { flexGrow: 1 }]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <View style={styles.content}>
            <Text style={styles.title}>{t("TELL_US_MORE_ABOUT_TRANSACTION")}</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 36}}>
              <View style={{flexDirection: 'row'}}>
                <Image source={EnglishFlag} style={{ width: 48, height: 48, borderRadius: 50, marginRight: 12}}/>
                <View>
                  <Text style={styles.companyName}>{String(selectedBillerInformation?.BillerName ?? selectedBillerInformation?.billerName ?? 'Select a biller')}</Text>
                  <Text style={styles.deliveryText}>{String(selectedBillerInformation?.BillerNotes ?? selectedBillerInformation?.billerNotes ?? ' ')}</Text>
                </View>
              </View>
              <TouchableOpacity>
                <Image source={RightArrow} style={{ width: 24, height: 24}} />
              </TouchableOpacity>
            </View>
            <View style={styles.textInputView}>
              <TextInput
                testID="billPayAccountNumber"
                style={styles.inputWrapper}
                placeholder={'Biller Account Number'}
                keyboardType="numeric"
                value={billerAccountNumber}
                onChangeText={(text) => {
                  setBillerAccountNumber(text);
                  try { setBillPayBillerAccountNumberStore?.(text || null); } catch {}
                }}
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </Wrapper>
    );
  }
);

export default AccountInformationStep;

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff",
    flex: 1
  },
  scrollContainer: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    alignItems: "stretch",
  },
  content: {
    marginTop: 8
  },
  title: {
    fontFamily: 'Rubik',
    fontSize: 28,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 36,
  },
  phoneInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 36,
  },
  countryCodeSelector: {
    height: 56,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
    paddingHorizontal: 16,
    marginRight: 8,
  },
  plusText: { fontSize: 14, fontWeight: "700", color: "#111827" },
  textInput: {
    flex: 1,
    height: 56,
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
    paddingHorizontal: 16,
    fontSize: 16,
    color: "#111827",
  },
  companyName: {
    fontSize: 16,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 24,
  },
  deliveryText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 16
  },
  textInputView: {
    backgroundColor: Colors.textInputBackground,
    padding: 16,
    borderRadius: 8,
    marginBottom: 36
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.textInputBackground,
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: "400",
    lineHeight: 24,
    color: '#737373'
  },
});